import {Component, OnDestroy, OnInit} from '@angular/core';
import {Apollo, gql} from 'apollo-angular';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {ToastrService} from 'ngx-toastr';
import {ErrorService} from '../../services/error.service';
import {catchError, EMPTY, exhaustMap, Subject, Subscription} from 'rxjs';
import {UntypedFormControl, Validators} from '@angular/forms';
import {TemplateService_GetTemplateItem_templateItem} from '../../services/__generated__/TemplateService_GetTemplateItem';
import {Router} from '@angular/router';
import {
  CopyBuilderTemplate_CopyBuilderTemplate,
  CopyBuilderTemplate_CopyBuilderTemplateVariables,
} from './__generated__/CopyBuilderTemplate_CopyBuilderTemplate';
import {CopyBuilderTemplateInput} from '../../graphql/globalTypes';

const COPY_TEMPLATE_MUTATION = gql`
  mutation CopyBuilderTemplate_CopyBuilderTemplate($input: CopyBuilderTemplateInput!) {
  copyBuilderTemplate(input: $input) {
    error
    templateItem {
      id
    }
  }
}`;

@Component({
  selector: 'app-template-copy',
  templateUrl: './template-copy.component.html',
  styleUrls: ['./template-copy.component.scss'],
  standalone: false,
})
export class TemplateCopyComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  titleInput = new UntypedFormControl(null, {validators: Validators.required});
  submit$ = new Subject<[string, string]>();
  templateItem: TemplateService_GetTemplateItem_templateItem | undefined;

  constructor(
    private bsModalRef: BsModalRef,
    private apollo: Apollo,
    private errService: ErrorService,
    private toastr: ToastrService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    if (this.bsModalRef.content) {
      this.templateItem = this.bsModalRef.content.templateItem;
    }

    this.subscriptions.push(
      this.submit$
        .pipe(
          exhaustMap(([id, title]) => {
            const mutationInput: CopyBuilderTemplateInput = {
              templateItemId: id,
              newTitle: title,
            };
            return this.apollo
              .mutate<
                CopyBuilderTemplate_CopyBuilderTemplate,
                CopyBuilderTemplate_CopyBuilderTemplateVariables
              >({
                mutation: COPY_TEMPLATE_MUTATION,
                variables: {
                  input: mutationInput,
                },
              })
              .pipe(
                catchError(error => {
                  this.errService.handleDefault(error);
                  return EMPTY;
                }),
              );
          }),
        )
        .subscribe(response => {
          const error = response.data?.copyBuilderTemplate.error;
          if (error) {
            this.toastr.error($localize`An error has occurred: ${error}.`);
          } else {
            this.toastr.success($localize`Template copied.`);
            const newTemplateId = response.data?.copyBuilderTemplate.templateItem?.id;
            if (newTemplateId) {
              const url = this.router.createUrlTree(['/templates/details', newTemplateId]);
              window.open(url.toString(), '_blank');
            }
          }
          this.bsModalRef.hide();
        }),
    );
  }

  submit(): void {
    this.titleInput.updateValueAndValidity();
    if (this.titleInput.invalid) {
      this.toastr.error($localize`Please enter a valid template name`);
      return;
    }
    if (!this.templateItem?.id) {
      this.toastr.error($localize`Template not found`);
      return;
    }
    this.submit$.next([this.templateItem.id, this.titleInput.value]);
  }

  closeModal(): void {
    this.bsModalRef.hide();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
